// src/beta/hooks/useIdleTimer.ts
import { useEffect, useRef } from 'react';

interface UseIdleTimerOptions {
  timeoutMs: number;
  onIdle: () => void;
  onActive: () => void;
}

const RESET_EVENTS = ['pointerdown', 'pointermove'] as const;

/** Beta-tree idle timer.
 *
 *  Fires `onIdle` after `timeoutMs` of pointer inactivity. Fires `onActive`
 *  on the first pointer event after `onIdle` has fired. Pointer events only —
 *  key, scroll, and wheel do NOT reset (kiosk has no keyboard, and stray
 *  scroll on iPad would reset constantly).
 *
 *  Adheres to the spec's "20s no touch → enter attract loop" requirement.
 *  Phase G hangs the actual attract animation off the `isAttract` flag this
 *  fires. Phase D just renders a pulsing red dot. */
export function useIdleTimer(opts: UseIdleTimerOptions): void {
  const { timeoutMs, onIdle, onActive } = opts;
  const idleRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);

  // Keep latest callbacks without retriggering the effect.
  useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);
  useEffect(() => { onActiveRef.current = onActive; }, [onActive]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const arm = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        idleRef.current = true;
        onIdleRef.current();
      }, timeoutMs);
    };

    const onPointer = () => {
      if (idleRef.current) {
        idleRef.current = false;
        onActiveRef.current();
      }
      arm();
    };

    RESET_EVENTS.forEach((evt) => {
      document.addEventListener(evt, onPointer, { passive: true });
    });
    arm();

    return () => {
      RESET_EVENTS.forEach((evt) => {
        document.removeEventListener(evt, onPointer);
      });
      if (timer !== null) clearTimeout(timer);
    };
  }, [timeoutMs]);
}
